import { useQuery } from '@tanstack/react-query';
import {
  getRevieweeListApi,
  getActionPlanListApi,
  createPOAOneOnOneAgendaApi,
  createPOACompetencyInitiativeApi,
  deleteActionPlanItemApi,
  addActionPlanItemApi,
  editActionPlanItemApi,
} from 'api/reviewActionPlan';
import { getCompetencyTasks } from 'components/Develop/dataHooks';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import type { NewActionPlanItem, EditedActionPlanItem } from '../types';

const getRevieweeList = async (reviewCycle) => {
  try {
    const revieweeList = await getRevieweeListApi(reviewCycle);
    return revieweeList;
  } catch (err) {
    console.error('Error fetching employee : ', err);
    throw err;
  }
};

const getActionPlanList = async ({ revieweeId, reviewCycle }) => {
  try {
    const actionPlanList = await getActionPlanListApi({ revieweeId, reviewCycle });
    return actionPlanList;
  } catch (err) {
    console.error('Error fetching plan of action : ', err);
    throw err;
  }
};

interface CreatePOAOneOnOneAgendaProps {
  newAgenda: { agendaText: string; referenceType: null };
  reviewCycle: string;
  actionPlanId: number;
  instanceId: number;
}

export const createPOAOneOnOneAgenda = async ({
  newAgenda,
  reviewCycle,
  actionPlanId,
  instanceId,
}: CreatePOAOneOnOneAgendaProps) => {
  try {
    const response = await createPOAOneOnOneAgendaApi({ newAgenda, reviewCycle, actionPlanId, instanceId });
    return response;
  } catch (error) {
    console.error('Error creating one on one agenda : ', error);
    throw error;
  }
};

interface UserProfile {
  uuid: string;
  ownerType: string;
  metadata: {
    displayName: string;
  };
  profile: {
    profilePhoto: string;
  };
}

interface CreatePOACompetencyInitiativeProps {
  newInitiative: { text: string; description: string; toIds: UserProfile[] };
  reviewCycle: string;
  actionPlanId: number;
  competencyId: number;
}

export const createPOACompetencyInitiative = async ({
  newInitiative,
  reviewCycle,
  actionPlanId,
  competencyId,
}: CreatePOACompetencyInitiativeProps) => {
  try {
    const initiative = {
      text: newInitiative.text,
      to: null,
      macros: {
        employees: {},
        demographics: {},
        attributes: {
          orgTag: {},
          money: null,
        },
      },
      from: null,
      dueDate: null,
      dueTS: new Date().valueOf(),
      teams: [],
      toIds: newInitiative.toIds,
      taskType: 'COMPETENCY_TASK',
      parentPost: null,
      type: 'TASK',
      actual: 0,
      visibilityType: 'PRIVATE',
      startTS: new Date().valueOf(),
      description: newInitiative.description,
      tags: null,
      startingProgressValue: 0,
      weightage: 0,
      removedTeam: null,
      parentPosts: null,
    };

    const response = await createPOACompetencyInitiativeApi({
      newInitiative: initiative,
      reviewCycle,
      actionPlanId,
      competencyId,
    });
    return response;
  } catch (error) {
    console.error('Error creating one on one agenda : ', error);
    throw error;
  }
};

export const useGetPOARevieweeList = (reviewCycle) =>
  useQuery([REACT_QUERY_KEYS.reviewActionPlan.revieweeList, reviewCycle], () => getRevieweeList(reviewCycle), {
    enabled: Boolean(reviewCycle),
    onError: (error) => error,
  });

export const useGetPOAList = ({ reviewCycle, revieweeId }) =>
  useQuery(
    [REACT_QUERY_KEYS.reviewActionPlan.actionPlanList, reviewCycle, revieweeId],
    () => getActionPlanList({ revieweeId, reviewCycle }),
    {
      enabled: Boolean(reviewCycle && revieweeId),
      onError: (error) => error,
    }
  );

export const useGetCompetencyInitiatives = ({
  employeeId,
  competencyId,
}: {
  employeeId: string;
  competencyId: string;
}) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.reviewActionPlan.competencyInitiatives, employeeId, competencyId],
    queryFn: () => getCompetencyTasks({ employeeId, competencyId }).then((res) => res.entity),
    enabled: Boolean(employeeId && competencyId),
  });

export const deleteActionPlanItem = async ({
  reviewCycle,
  actionPlanId,
}: {
  reviewCycle: string;
  actionPlanId: number;
}) => {
  try {
    const response = await deleteActionPlanItemApi({ reviewCycle, actionPlanId });
    return response;
  } catch (error) {
    console.error('Error deleting plan of action : ', error);
    throw error;
  }
};

export const addActionPlanItem = async ({
  newActionPlanItem,
  reviewCycle,
  revieweeId,
}: {
  newActionPlanItem: NewActionPlanItem;
  reviewCycle: string;
  revieweeId: string;
}) => {
  try {
    const response = await addActionPlanItemApi({ newActionPlanItem, reviewCycle, revieweeId });
    return response;
  } catch (error) {
    console.error('Error creating action plan item : ', error);
    throw error;
  }
};

export const editActionPlanItem = async ({
  editedActionPlanItem,
  reviewCycle,
}: {
  editedActionPlanItem: EditedActionPlanItem;
  reviewCycle: string;
}) => {
  try {
    const response = await editActionPlanItemApi({ editedActionPlanItem, reviewCycle });
    return response;
  } catch (error) {
    console.error('Error creating action plan item : ', error);
    throw error;
  }
};
